'use client';

import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@ui/components/ui/dialog';
import { Progress } from '@ui/components/ui/progress';
import { useTranslations } from 'next-intl';
import { WelcomeStep, AccountSetupStep, JobDescriptionStep } from './steps';
import Logo from '@ui/components/ui/logo';
import { useOnboarding } from '@/contexts/onboarding-context';
import { useApp } from '@/contexts/app-context';
import { motion, AnimatePresence } from 'framer-motion';

export function OnboardingDialog() {
  const t = useTranslations('Onboarding');
  const { currentStep, completeStep, goBackStep } = useOnboarding();
  const { loading, startTransition } = useApp();

  const steps = ['welcome', 'account_setup', 'job_description'];
  const currentStepIndex = steps.indexOf(currentStep);
  const totalSteps = steps.length;
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;

  const handleStepComplete = (step: string, data?: any) => {
    startTransition(async () => {
      await completeStep(step, data);
    });
  };

  const handleGoBack = () => {
    startTransition(async () => {
      await goBackStep();
    });
  };

  // Respect user's motion preferences
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep onComplete={(data: any) => handleStepComplete('welcome', data)} isLoading={loading} />;
      case 'account_setup':
        return <AccountSetupStep onComplete={(data: any) => handleStepComplete('account_setup', data)} onBack={handleGoBack} isLoading={loading} />;
      case 'job_description':
        return (
          <JobDescriptionStep
            onComplete={(data: any) => handleStepComplete('job_description', data)}
            onSkip={() => handleStepComplete('job_description', { skipped: true })}
            onBack={handleGoBack}
            isLoading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open modal>
      <DialogContent className="min-w-full h-screen max-h-screen overflow-y-auto p-4 md:p-8 lg:p-16 rounded-none" showCloseButton={false}>
        <DialogTitle className="hidden">Onboarding</DialogTitle>
        <DialogDescription className="hidden">Complete the onboarding steps to get started</DialogDescription>
        <div className="flex flex-col min-h-full gap-4 md:gap-12">
          {/* Progress bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 justify-center items-center shrink-0 gap-2">
            <Logo className="hidden md:flex" />
            <Progress value={progress} />
            <div className="text-sm text-right">{t('progress.step', { current: currentStepIndex + 1, total: totalSteps })}</div>
          </div>

          <div className="w-full max-w-5xl mx-auto flex-1 overflow-y-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: prefersReducedMotion ? 0 : 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: prefersReducedMotion ? 0 : -100 }}
                transition={{
                  type: prefersReducedMotion ? 'tween' : 'spring',
                  stiffness: 300,
                  damping: 30,
                  duration: prefersReducedMotion ? 0.2 : undefined,
                }}
              >
                {renderCurrentStep()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
